<script>

    // CartItemTile
    // A tile for an item in the cart.

    // Setup ---
    // Use the values below to modify how the tile appears
    const setup = {
        tileHeight: '8rem',           // Height of the full tab, fixed for alignment purposes
        productNameLines: 2            // Number of lines product name is allowed to wrap before being trimmed with ellipses
    };

    export const Setup = setup;

</script>

<script setup>

    // Imports ----
    import { reactive, defineProps } from 'vue';
    import { store } from '@/Store';
    import LimitMessages from '@/site/components/LimitMessages';
    import { UpdateCartItemQtyForm } from '@dd-nucleus/nucleus-vue';

    // Props ----
    const props = defineProps({
        // The cart item
        item: {
            type: Object,
            required: true
        },
        editable: {
            type: Boolean,
            required: false,
            default: true
        },
        removable: {
            type: Boolean,
            required: false,
            default: true
        }
    });

    // State ----
    const form = reactive(new UpdateCartItemQtyForm(props.item));

    // Handlers ----
    async function onRemoveFromCart() {
        await store.cart.removeItem(props.item.id);
    }
</script>

<template>

    <div class="cart-item-tile" :style="{ 'height': setup.tileHeight }">
        <div v-if="removable" class="float-end remove"><ActionButton icon="bi bi-x-lg" label="Remove" @click="onRemoveFromCart" /></div>
        <div class="inner-row d-flex flex-row">
            <div class="left">
                <ProductThumbnail :product="props.item.product" :height="setup.tileHeight" no-image-available-url="/images/no-image.png" />
            </div>
            <div class="middle flex-fill">
                <!-- Stretchy middle part-->
                <div class="tags mb-2">
                    <ProductTags :product="props.item.product" />
                </div>

                <!-- Product name and item number -->
                <router-link class="title-link" :to="`/product/${props.item.product.id}`">
                    <div class="name" v-snip="{ lines: setup.productNameLines }" :title="item.product.name">
                        {{ props.item.product.name }}
                    </div>
                </router-link>
                <div class="item-number"><router-link class="title-link" :to="`/product/${props.item.product.id}`">{{ props.item.product.itemNumber }}</router-link></div>

                <div class="favorite">
                    <ProductFavoriteIcon :product="props.item.product" label="Add as Favorite" selected-label="Favorite" />
                </div>
            </div>
            <!-- Fixed right panel -->
            <div class="right d-flex flex-column" :style="{ height: setup.tileHeight }">
                <FormContainer :form="form">
                    <div class="flex-fill">
                        <CartItemQty :item="props.item" :editable="props.editable" />
                        <FormMessages field-name="quantity" :add-box="false" class="mt-2" />
                        <FieldMessages field-name="quantity" class="mt-2" />
                        <LimitMessages :product="item.product" class="mt-2" />
                    </div>
                </FormContainer>
                <div class="messages">
                    <MessageList :messages="props.item.messages" :add-box="false" />
                </div>
            </div>
        </div>
    </div>

</template>

<style lang="scss">

    .cart-item-tile {
        .inner-row {
            border-bottom: 1px solid $gray-20;
            padding-bottom: 1rem;
        }

        .left {
            width: 8rem;
        }

        .name {
            font-size: 1.0rem;
            line-height: 1.1rem;
        }

        a.title-link, a.title-link:visited, a.title-link:hover {
            color: var(--bs-body-color);
            text-decoration: none;
        }

        .item-number a.title-link {
            font-size: 0.7rem;
            color: $gray-50;
        }

        .n-product-add-to-cart-qty .text-box {
            border-color: #dddddd;
        }

        .btn[disabled] {
            opacity: 0.2;
        }

        .middle {
            padding-left: 1rem;
        }

        .favorite {
            margin-top: 1rem;
        }

        .right {
            width: 14rem;
        }

        .remove {
            margin-top: 0.5rem;
        }
    }

</style>